'use client'

import { useRouter } from 'next/router'
import * as React from 'react'

import memeService from '@/services/meme.service'
import { createContext } from '@/utils/context'

import { MemePost } from '@/types/meme.interface'

interface MemePostModalContextValue {
  isOpen: boolean
  currentPost?: MemePost
  openModal: (post: MemePost) => void
  openModalWithId: (postId: string) => Promise<MemePost | null>
  closeModal: () => void
  setCurrentPost: (post: MemePost) => void
}

const [MemePostModalProvider, useMemePostModal] = createContext<MemePostModalContextValue>('MemePostModal')

function MemePostModalProviderWrapper({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [currentPost, setCurrentPost] = React.useState<MemePost>()
  const router = useRouter()

  const openModal = React.useCallback(
    (post: MemePost) => {
      setCurrentPost(post)

      router
        .replace(
          {
            pathname: router.pathname,
            query: { ...router.query, postId: post.id },
          },
          undefined,
          { shallow: true }
        )
        .then(() => {
          setIsOpen(true)
        })
    },
    [router]
  )

  const openModalWithId = React.useCallback(async (postId: string) => {
    try {
      const post = await memeService.getMemeDetail(postId)
      setCurrentPost(post)
      setIsOpen(true)
      return post
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch shared post:', error)
      return null
    }
  }, [])

  const closeModal = React.useCallback(() => {
    /* eslint-disable unused-imports/no-unused-vars */
    const { postId, ...restQuery } = router.query
    // Close immediately for responsive UX; sync URL separately.
    setCurrentPost(undefined)
    setIsOpen(false)

    void router.replace(
      {
        pathname: router.pathname,
        query: restQuery,
      },
      undefined,
      { shallow: true }
    )
  }, [router])

  const value = React.useMemo(
    () => ({
      isOpen,
      currentPost,
      openModal,
      openModalWithId,
      closeModal,
      setCurrentPost,
    }),
    [isOpen, currentPost, openModal, openModalWithId, closeModal, setCurrentPost]
  )

  return <MemePostModalProvider {...value}>{children}</MemePostModalProvider>
}

export { MemePostModalProviderWrapper as MemePostModalProvider, useMemePostModal }
